import React, { useMemo, useState, useEffect, useRef } from 'react';
import percentageTaxAPI from '../api/percentageTaxAPI';
import ChartOfAccountsSearch from './ChartOfAccountsSearch';
import MoneyInput from './MoneyInput';
import AtcSearchSelect from './AtcSearchSelect';
import CustomerSearch from './CustomerSearch';
import VendorSearch from './VendorSearch';
import RestoreDraftModal from './RestoreDraftModal';
import SaveOutcomePanel from './SaveOutcomePanel';
import DraftStatusIndicator from './DraftStatusIndicator';
import notify from '../lib/notify';
import useGuardedMutation from '../hooks/useGuardedMutation';
import useEncodingDraft from '../hooks/useEncodingDraft';
import { DRAFT_SCREENS } from '../utils/encodingDraft';
import { setPendingIdempotencyKey } from '../utils/idempotencyKey';
import { isCorporateFiler } from '../utils/sawtFormType';

// Mirrors VATInputForm's mapping so a vendor classifies identically in both
// forms. Vendor.businessType also allows 'partnership' and 'government', which
// both fall to 'individual' here — see the note in the session report.
const getVendorClassification = (businessType) =>
  businessType === 'corporation' ? 'corporation' : 'individual';

// Percentage tax rate for a sales line. Independent of any ATC — see
// handleAtcChange.
const DEFAULT_TAX_RATE = 3;

// True when a row already carries certificate data. Used so a row loaded with
// an existing ATC/counterparty opens with the toggle ON rather than hiding data
// the user can see was saved.
const hasCertificateData = (row) =>
  Boolean(
    row?.atc
      || row?.counterpartyName
      || row?.counterpartyTin
      || row?.counterpartyClassification
      || row?.customer
      || row?.vendor
  );

// Module scope so the autosave hook can hold a stable reference to it: the draft
// layer and the save path must agree on what "this row is untouched" means.
const isRowEmpty = (r) => {
  const hasAny =
    !!r.month ||
    !!r.chartOfAccount ||
    (Number(r.amount) || 0) > 0 ||
    !!r.chartOfAccountName;
  return !hasAny;
};

/**
 * Identifies a submitted row in the server's echo of what it saved.
 * insertMany({ ordered: false }) can drop rows and still return 201, so the
 * only way to know WHICH rows survived is to match the returned documents back
 * against what we sent. Month + account + amount is the natural key here; two
 * genuinely identical lines are handled by consuming matches one at a time.
 */
const rowMatchKey = (r) =>
  `${Number(r.month)}|${r.chartOfAccount || ''}|${Number(r.amount) || 0}`;

/**
 * Pulls the saved-records array out of a create response, or null when the
 * shape isn't one we recognise. null means "don't reconcile counts" — a 2xx is
 * still the server confirming the write; we just can't second-guess it row by
 * row.
 */
const extractSavedRecords = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response)) return response;
  return null;
};

// NOTE: taxRate default changes depending on category, so we won't use this directly
const makeEmptyRow = (category) => ({
  id: Date.now() + Math.random(),
  month: '',
  chartOfAccount: null,
  chartOfAccountName: '',
  amount: 0,
  taxRate: category === 'income' ? 0 : DEFAULT_TAX_RATE,
  taxDue: 0,
  // Whether this line is backed by a 2306/2307. Off for a new row; a row
  // arriving with saved certificate data shows ON via hasCertificateData().
  hasCertificate: false,
  atc: null,
  customer: null,
  vendor: null,
  counterpartyName: '',
  counterpartyTin: '',
  counterpartyClassification: '',
  description: '',
  transactionDate: new Date().toISOString().split('T')[0],
});

const PercentageTaxForm = ({ client, onRecordAdded, selectedYear, category = 'sales' }) => {
  const [fiscalYear, setFiscalYear] = useState(selectedYear || new Date().getFullYear());
  const createEmptyRows = () => Array.from({ length: 5 }, () => makeEmptyRow(category));

  const [rows, setRows] = useState(createEmptyRows);
  const [clearTrigger, setClearTrigger] = useState(0);
  // Per-quarter result of the last submit. Replaces the old "N record(s) saved
  // successfully" banner, which counted local rows and so reported a partly
  // saved batch as a clean one.
  const [outcomes, setOutcomes] = useState([]);

  const draft = useEncodingDraft({
    screen: category === 'income' ? DRAFT_SCREENS.PT_INCOME : DRAFT_SCREENS.PT_SALES,
    clientId: client?._id,
    fiscalYear,
    rows,
    isRowEmpty,
  });

  useEffect(() => {
    if (selectedYear) setFiscalYear(selectedYear);
  }, [selectedYear]);

  // if category prop changes, clear rows to respect new defaults
  useEffect(() => {
    resetRows();
  }, [category]);

  // The counterparty is a different real-world party per category, not just a
  // different label: on a sale the client is paid and the counterparty withheld
  // (they are the PAYOR, who issues the 2307); on an expense the client paid and
  // withheld (the counterparty is the PAYEE, named on the 2306).
  const counterpartyLabel = category === 'income' ? 'Payee' : 'Payor';

  // ATC codes key to the PAYEE, so the direction above decides what narrows the
  // list. On a 2307 the payee is the FILER, so the class follows the client's
  // own entity type — narrowing by the payor here would offer the wrong half of
  // every WI###/WC### pair. On a 2306 the payee is the counterparty itself.
  // Matches Form2307Form.jsx, which derives it the same way.
  const filerClass = isCorporateFiler(client?.clientType) ? 'corporation' : 'individual';

  const taxRates = useMemo(
    () => [
      { value: 1, label: '1%' },
      { value: 2, label: '2%' },
      { value: 3, label: '3% (default)' },
      { value: 5, label: '5%' },
      { value: 10, label: '10%' },
    ],
    []
  );

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: String(i + 1),
        label: new Date(0, i).toLocaleString('en-US', { month: 'short' }),
      })),
    []
  );

  const getQuarterFromMonth = (m) => {
    if (!m) return null;
    const mi = parseInt(m, 10);
    if (Number.isNaN(mi) || mi < 1 || mi > 12) return null;
    return Math.floor((mi - 1) / 3) + 1;
  };

  const recalcTaxDue = (amount, rate) => {
    if (category === 'income') return 0;
    const a = Number(amount) || 0;
    const r = Number(rate) || 0;
    return Number(((a * r) / 100).toFixed(2));
  };

  const setRowField = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  /**
   * Auto-fill the counterparty from a picked Vendor (expenses / 2306).
   *
   * The picker is a convenience, never a requirement: the denormalized fields
   * stay editable afterwards, and clearing it (onSelect(null)) returns the row
   * to a blank manual state without disturbing anything else.
   */
  const handleVendorSelect = (idx, vendor) => {
    if (!vendor) {
      setRowField(idx, {
        vendor: null,
        counterpartyName: '',
        counterpartyTin: '',
        counterpartyClassification: '',
      });
      return;
    }
    setRowField(idx, {
      vendor,
      counterpartyName: vendor.name || '',
      counterpartyTin: vendor.tin || '',
      counterpartyClassification: getVendorClassification(vendor.businessType),
    });
  };

  /**
   * Auto-fill the counterparty from a picked Customer (sales / 2307).
   *
   * Name and TIN ONLY. Customer has no legal-entity type — `customerType` is a
   * channel tag ('pos' | 'accounting' | ...), and the search endpoint does not
   * return a businessType at all. Deriving a classification from it is the bug
   * that silently stamps every VAT Output record 'individual', so the
   * classification here stays manual and is deliberately left untouched on both
   * select and clear.
   */
  const handleCustomerSelect = (idx, customer) => {
    if (!customer) {
      setRowField(idx, { customer: null, counterpartyName: '', counterpartyTin: '' });
      return;
    }
    setRowField(idx, {
      customer,
      counterpartyName: customer.name || '',
      counterpartyTin: customer.tin || '',
    });
  };

  const handleMonthChange = (idx, month) => setRowField(idx, { month });

  const handleAmountChange = (idx, val) => {
    setRows((prev) => {
      const next = [...prev];
      const row = next[idx];
      const taxDue = recalcTaxDue(val, row.taxRate);
      next[idx] = { ...row, amount: val, taxDue };
      return next;
    });
  };

  /**
   * Selecting an ATC must NOT touch taxRate.
   *
   * These are two unrelated tax systems: taxRate is the percentage tax of
   * Sections 116-127 (2551Q, PT### codes, 3% default), while the ATC here is a
   * 2306/2307 WITHHOLDING code whose rate belongs to a different tax on a
   * different party. An earlier revision seeded taxRate from the ATC's rate;
   * that was wrong per BIR and has been reverted. Do not reintroduce it in any
   * form, including as a "suggested default".
   */
  const handleAtcChange = (idx, atc) => {
    setRowField(idx, { atc });
  };

  /**
   * Show/hide the certificate half of the row.
   *
   * Turning it OFF wipes every certificate field, so a row saved with the
   * toggle off can never carry stale ATC or counterparty data. Turning it back
   * ON therefore starts blank rather than restoring what was cleared — see the
   * session report for why that was chosen over stashing the old values.
   */
  const handleHasCertificateChange = (idx, checked) => {
    if (checked) {
      setRowField(idx, { hasCertificate: true });
      return;
    }
    setRowField(idx, {
      hasCertificate: false,
      atc: null,
      customer: null,
      vendor: null,
      counterpartyName: '',
      counterpartyTin: '',
      counterpartyClassification: '',
    });
  };

  const handleRateChange = (idx, rate) => {
    if (category === 'income') return; // ignore
    setRows((prev) => {
      const next = [...prev];
      const row = next[idx];
      const taxDue = recalcTaxDue(row.amount, rate);
      next[idx] = { ...row, taxRate: rate, taxDue };
      return next;
    });
  };

  const handleCOASelect = (idx, account) => {
    if (!account) {
      setRowField(idx, { chartOfAccount: null, chartOfAccountName: '' });
      return;
    }

    const label = `${account.accountNumber || account.accountCode || ''} - ${account.accountName || ''}`.trim();
    setRowField(idx, { chartOfAccount: account._id, chartOfAccountName: label });
  };

  const validateRow = (r, rowIndexForMsg) => {
    if (isRowEmpty(r)) return null; // discarded
    if (!r.month) return `Row ${rowIndexForMsg}: Please select a month.`;
    if (!r.chartOfAccount) return `Row ${rowIndexForMsg}: Please select a chart of account.`;
    // ATC is intentionally not required: a line legitimately has none when no
    // 2307/2306 was issued against it.
    if (!r.amount || Number(r.amount) <= 0) return `Row ${rowIndexForMsg}: Amount must be greater than 0.`;
    const q = getQuarterFromMonth(r.month);
    if (!q) return `Row ${rowIndexForMsg}: Invalid month.`;
    return null;
  };

  const resetRows = () => setRows(createEmptyRows());

  const addRow = () => setRows((prev) => [...prev, makeEmptyRow(category)]);

  const removeRow = (id) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((r) => r.id !== id);
    });
  };

  const submitRows = async () => {
    if (!client?._id) return;

    // validate
    for (let i = 0; i < rows.length; i++) {
      const err = validateRow(rows[i], i + 1);
      if (err) {
        notify.error("Couldn't save", err);
        return;
      }
    }

    const filled = rows.filter((r) => !isRowEmpty(r));
    if (filled.length === 0) {
      notify.error("Couldn't save", 'Fill at least one line before saving.');
      return;
    }

    // Build payloads grouped by quarter (because API expects quarter param).
    // rowIds travel alongside so an outcome can be traced back to the rows on
    // screen — which is what lets a failed quarter keep its rows while a saved
    // one clears.
    const grouped = new Map(); // quarter -> { records[], rowIds[] }
    filled.forEach((r) => {
      const quarter = getQuarterFromMonth(r.month);
      const record = {
        // ✅ simplified model fields (recommended)
        month: Number(r.month),
        amount: Number(r.amount) || 0,
        taxRate: category === 'income' ? 0 : Number(r.taxRate) || 0,
        taxDue: category === 'income' ? 0 : Number(r.taxDue) || 0,
        chartOfAccount: r.chartOfAccount,
        chartOfAccountName: r.chartOfAccountName,
        atc: r.atc?._id || null,
        customer: r.customer?._id || null,
        vendor: r.vendor?._id || null,
        counterpartyName: r.counterpartyName || '',
        counterpartyTin: r.counterpartyTin || '',
        counterpartyClassification: r.counterpartyClassification || '',
        description: r.description || '',
        category, // add category from parent tab
      };

      if (!grouped.has(quarter)) grouped.set(quarter, { records: [], rowIds: [] });
      grouped.get(quarter).records.push(record);
      grouped.get(quarter).rowIds.push(r.id);
    });

    const allSavedRecords = [];
    const results = [];
    const confirmedRowIds = [];

    // Each quarter is an independent POST, so one failing quarter must not
    // abandon the rest — the old loop threw out of the whole submit on the
    // first error, silently stranding quarters that would have saved fine.
    for (const [quarter, group] of grouped.entries()) {
      const groupId = `fy${fiscalYear}-q${quarter}-${category}`;
      const label = `Q${quarter} ${fiscalYear}`;

      // Same key on a retry of the same content, so a resubmission after a
      // dropped response replays instead of inserting the quarter twice. The
      // payload here is fully deterministic (no timestamp), so the body a retry
      // sends is byte-identical to the original.
      setPendingIdempotencyKey(draft.keyForGroup(groupId, group.records).key);

      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await percentageTaxAPI.createRecords(
          client._id,
          group.records,
          quarter,
          fiscalYear
        );

        const savedRecords = extractSavedRecords(response);

        if (savedRecords === null) {
          // 2xx with a body we can't reconcile — the server still confirmed it.
          results.push({ id: groupId, label, status: 'saved' });
          confirmedRowIds.push(...group.rowIds);
          draft.releaseKey(groupId);
        } else if (savedRecords.length >= group.records.length) {
          results.push({
            id: groupId,
            label,
            status: 'saved',
            savedCount: savedRecords.length,
            sentCount: group.records.length,
          });
          confirmedRowIds.push(...group.rowIds);
          allSavedRecords.push(...savedRecords);
          draft.releaseKey(groupId);
        } else {
          // Defensive: the route is now all-or-nothing (it 400s on any invalid
          // line), but if a 2xx ever echoes fewer rows than were sent, match
          // the echo back to what we sent so only the rows that landed are cleared.
          const echoed = new Map();
          savedRecords.forEach((doc) => {
            const k = rowMatchKey(doc);
            echoed.set(k, (echoed.get(k) || 0) + 1);
          });

          const landed = [];
          group.records.forEach((record, i) => {
            const k = rowMatchKey(record);
            const remaining = echoed.get(k) || 0;
            if (remaining > 0) {
              echoed.set(k, remaining - 1);
              landed.push(group.rowIds[i]);
            }
          });

          results.push({
            id: groupId,
            label,
            status: 'partial',
            savedCount: savedRecords.length,
            sentCount: group.records.length,
            message:
              'The server accepted some lines and dropped others. The lines it did not confirm are still below.',
          });
          confirmedRowIds.push(...landed);
          allSavedRecords.push(...savedRecords);
          // Key deliberately kept: the unconfirmed rows in this quarter are
          // still candidates for a replay of this exact request.
        }
      } catch (err) {
        console.error('[PercentageTax] save failed for quarter', quarter, err);

        // A rejected request definitely saved nothing and is safe to resubmit.
        // No response at all means we genuinely do not know — say so rather
        // than guessing in either direction.
        const status = err?.hasResponse ? 'failed' : 'unknown';
        results.push({
          id: groupId,
          label,
          status,
          sentCount: group.records.length,
          message: err?.hasResponse
            ? err.message || 'The server rejected these lines.'
            : 'No response from the server — these lines may or may not have been saved.',
        });
      }
    }

    setOutcomes(results);

    const confirmed = new Set(confirmedRowIds.map(String));
    const everythingSaved = results.length > 0 && results.every((r) => r.status === 'saved');

    if (everythingSaved) {
      setClearTrigger((prev) => prev + 1); // wipes the COA pickers
      resetRows();
    } else if (confirmed.size > 0) {
      // Keep the unconfirmed rows exactly as typed so a retry needs no
      // re-keying. clearTrigger is NOT bumped — it would blank the account
      // pickers on the rows we are deliberately keeping.
      setRows((prev) => {
        const kept = prev.filter((r) => !confirmed.has(String(r.id)));
        return kept.length > 0 ? kept : createEmptyRows();
      });
    }

    // Only rows the server confirmed leave the local draft. Everything failed
    // or unknown stays, so a refresh still has it.
    draft.clearConfirmedRows(confirmedRowIds);

    const savedTotal = results
      .filter((r) => r.status === 'saved')
      .reduce((n, r) => n + (r.sentCount ?? 0), 0);

    if (everythingSaved) {
      notify.success('Saved', `${filled.length} percentage tax record(s) for FY ${fiscalYear}.`);
    } else {
      const unresolved = results.filter((r) => r.status !== 'saved').map((r) => r.label);
      notify.error(
        savedTotal > 0 ? 'Partly saved' : "Couldn't save",
        `${unresolved.join(', ')} ${unresolved.length === 1 ? 'was' : 'were'} not confirmed — see the details above the grid. Nothing you typed has been lost.`
      );
    }

    // Separated from the save itself: this refetches and auto-posts to GL, and
    // a failure here says nothing about whether the records saved. Folding it
    // into the save's catch is what used to report "Couldn't save" for records
    // that were already safely stored.
    try {
      if (allSavedRecords.length > 0) {
        for (const savedRecord of allSavedRecords) {
          // eslint-disable-next-line no-await-in-loop
          await onRecordAdded?.(savedRecord);
        }
      } else {
        await onRecordAdded?.();
      }
    } catch (refreshErr) {
      console.error('[PercentageTax] post-save refresh failed', refreshErr);
      notify.error(
        'Saved — refresh failed',
        'The records were saved, but the list could not be refreshed. Reload the page to see them.'
      );
    }
  };

  const { mutate: runSubmit, isPending: submitting } = useGuardedMutation(submitRows);

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Each request has its own error handling above; this catches anything that
    // goes wrong around them (building the payload, say) so it surfaces as a
    // message instead of an unhandled rejection that leaves the button spinning.
    runSubmit()?.catch((err) => {
      console.error('[PercentageTax] unexpected error while saving', err);
      notify.error(
        "Couldn't save",
        'Something went wrong before the records could be sent. Your rows are still here.'
      );
    });
  };

  const handleRestoreDraft = () => {
    const restored = draft.restoreDraft();
    if (restored?.length) {
      // clearTrigger is deliberately NOT bumped: the restored rows carry their
      // own ids, so the account pickers remount and repopulate from
      // chartOfAccountName — bumping it would fire their clear effect and blank
      // the very values we just restored.
      setRows(restored);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-teal-700">Percentage Tax (2551Q)</h3>
        <p className="text-sm text-gray-600">Add as many lines as needed — empty lines are discarded on save</p>
      </div>

      <RestoreDraftModal
        draft={draft.pendingDraft}
        onRestore={handleRestoreDraft}
        onDiscard={draft.discardDraft}
        screenLabel={`Percentage Tax (${category === 'income' ? 'Expenses' : 'Sales'})`}
      />

      <SaveOutcomePanel outcomes={outcomes} onDismiss={() => setOutcomes([])} />

      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Fiscal Year</span>
          <input
            type="number"
            value={fiscalYear}
            onChange={(e) => setFiscalYear(parseInt(e.target.value || '0', 10))}
            className="border rounded px-3 py-2 text-sm w-28"
            min="2000"
            max="2100"
            required
            disabled={submitting}
          />
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            resetRows();
          }}
          className="ml-auto text-sm text-gray-600 hover:text-gray-900"
          disabled={submitting}
        >
          Clear All
        </button>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chart of Account</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                2306/2307
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {counterpartyLabel}
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ATC</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              {category !== 'income' && (
                <>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">%</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tax Due</th>
                </>
              )}
              <th className="px-3 py-2 w-8"></th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((r, idx) => {
              // A row that arrived with saved certificate data shows ON even if
              // the flag was never set, so existing data is never hidden.
              const showCertificate = r.hasCertificate || hasCertificateData(r);
              return (
              <tr key={r.id ?? idx} className="hover:bg-gray-50 align-top">
                <td className="px-3 py-2 whitespace-nowrap align-top">
                  <select
                    value={r.month}
                    onChange={(e) => handleMonthChange(idx, e.target.value)}
                    className="border rounded px-2 py-1 text-sm w-28"
                    disabled={submitting}
                  >
                    <option value="">—</option>
                    {monthOptions.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </td>

                <td className="px-3 py-2 min-w-[280px] align-top">
                  <ChartOfAccountsSearch
                    value={r.chartOfAccountName}
                    onChange={() => {}}
                    onSelect={(account) => handleCOASelect(idx, account)}
                    placeholder="Search chart of accounts..."
                    showLinkedBadge={false}
                    disabled={submitting}
                    clearTrigger={clearTrigger}
                  />
                </td>

                <td className="px-3 py-2 align-top">
                  <label className="flex items-center gap-2 text-xs text-ink-body">
                    <input
                      type="checkbox"
                      checked={showCertificate}
                      onChange={(e) => handleHasCertificateChange(idx, e.target.checked)}
                      className="h-4 w-4 rounded border-ink-line text-primary-500 focus:ring-primary-500"
                      aria-label={`Row ${idx + 1} has a 2306/2307 certificate`}
                      disabled={submitting}
                    />
                    <span className="whitespace-nowrap">Has cert.</span>
                  </label>
                </td>

                <td className={`px-3 py-2 align-top ${showCertificate ? 'min-w-[220px]' : ''}`}>
                  {!showCertificate ? (
                    <span className="text-xs text-gray-400">—</span>
                  ) : (
                  <div className="space-y-1">
                    {/*
                      Optional shortcut, not a gate: the manual fields below are
                      always usable, and a row saves fine with the picker never
                      touched. On an expense the counterparty is a Vendor, whose
                      businessType gives a real classification; on a sale it is a
                      Customer, which carries no legal-entity type — so that
                      picker fills name and TIN only.
                    */}
                    {category === 'income' ? (
                      <VendorSearch
                        value={r.vendor}
                        onSelect={(vendor) => handleVendorSelect(idx, vendor)}
                        placeholder="Search payee (optional)"
                        disabled={submitting}
                      />
                    ) : (
                      <CustomerSearch
                        value={r.customer}
                        onSelect={(customer) => handleCustomerSelect(idx, customer)}
                        placeholder="Search payor (optional)"
                        disabled={submitting}
                      />
                    )}

                    <input
                      type="text"
                      value={r.counterpartyName}
                      onChange={(e) => setRowField(idx, { counterpartyName: e.target.value })}
                      className="border rounded px-2 py-1 text-sm w-full"
                      placeholder={`${counterpartyLabel} name (optional)`}
                      aria-label={`${counterpartyLabel} name`}
                      disabled={submitting}
                    />
                    <input
                      type="text"
                      value={r.counterpartyTin}
                      onChange={(e) => setRowField(idx, { counterpartyTin: e.target.value })}
                      className="border rounded px-2 py-1 text-sm w-full"
                      placeholder={`${counterpartyLabel} TIN`}
                      aria-label={`${counterpartyLabel} TIN`}
                      disabled={submitting}
                    />
                    <select
                      value={r.counterpartyClassification}
                      onChange={(e) =>
                        setRowField(idx, { counterpartyClassification: e.target.value })
                      }
                      className="border rounded px-2 py-1 text-sm w-full"
                      aria-label={`${counterpartyLabel} classification`}
                      disabled={submitting}
                    >
                      <option value="">— Classification —</option>
                      <option value="corporation">Corporation</option>
                      <option value="individual">Individual</option>
                    </select>
                  </div>
                  )}
                </td>

                <td className={`px-3 py-2 align-top ${showCertificate ? 'min-w-[240px]' : ''}`}>
                  {!showCertificate ? (
                    <span className="text-xs text-gray-400">—</span>
                  ) : (
                    <>
                      {/*
                        Sales are creditable withholding (2307); expenses are
                        final withholding (2306) — the same split the VAT forms
                        use. The rate on these codes is a WITHHOLDING rate and is
                        deliberately never copied into taxRate.
                      */}
                      <AtcSearchSelect
                        inputId={`atc-pt-${r.id}`}
                        formType={category === 'income' ? '2306' : '2307'}
                        payeeClass={
                          category === 'income'
                            ? r.counterpartyClassification || undefined
                            : filerClass
                        }
                        value={r.atc?._id || null}
                        onChange={(atc) => handleAtcChange(idx, atc)}
                        disabled={submitting}
                        placeholder="Optional — search ATC…"
                      />
                    </>
                  )}
                </td>

                <td className="px-3 py-2 whitespace-nowrap min-w-[200px] align-top">
                  <input
                    type="text"
                    value={r.description}
                    onChange={(e) => setRowField(idx, { description: e.target.value })}
                    className="border rounded px-2 py-1 text-sm w-full"
                    placeholder="Description"
                    disabled={submitting}
                  />
                </td>

                <td className="px-3 py-2 whitespace-nowrap min-w-[150px] align-top">
                  <MoneyInput
                    value={r.amount}
                    onValueChange={(val) => handleAmountChange(idx, val)}
                    placeholder="0.00"
                    disabled={submitting}
                  />
                </td>

                {category !== 'income' && (
                  <>
                    <td className="px-3 py-2 whitespace-nowrap min-w-[130px] align-top">
                      <select
                        value={String(r.taxRate)}
                        onChange={(e) => handleRateChange(idx, Number(e.target.value))}
                        className="border rounded px-2 py-1 text-sm w-full"
                        disabled={submitting}
                      >
                        {taxRates.map((rate) => (
                          <option key={rate.value} value={String(rate.value)}>
                            {rate.label}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap min-w-[150px] align-top">
                      <MoneyInput value={r.taxDue} disabled placeholder="0.00" />
                    </td>
                  </>
                )}

                {/* Remove row */}
                <td className="px-2 py-2 text-center">
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(r.id)}
                      className="text-red-400 hover:text-red-600 text-xl leading-none"
                      title="Remove line"
                      disabled={submitting}
                    >
                      ×
                    </button>
                  )}
                </td>

              </tr>
              );
            })}
          </tbody>

          <tfoot className="bg-gray-50">
            <tr>
              <td className="px-3 py-2 text-sm text-gray-500 align-top" colSpan={7}>
                {rows.length} line(s) — empty rows discarded on save
              </td>
              {category !== 'income' ? (
                <td className="px-3 py-2 text-sm font-medium text-gray-900 align-top" colSpan={2}>
                  Total:{' '}
                  {Number(
                    rows.reduce((sum, r) => sum + (Number(r.taxDue) || 0), 0).toFixed(2)
                  ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              ) : null}
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="pt-2 flex items-center gap-3">
        <button
          type="button"
          onClick={addRow}
          disabled={submitting}
          className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-800 border border-teal-300 rounded px-3 py-1.5 hover:bg-teal-50 disabled:opacity-50"
        >
          + Add Line
        </button>

        <DraftStatusIndicator lastSavedAt={draft.lastSavedAt} isSaving={draft.isSaving} />

        <button
          type="submit"
          className="ml-auto bg-teal-600 hover:bg-teal-700 text-white px-5 py-2 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          disabled={submitting || rows.every((r) => isRowEmpty(r))}
        >
          {submitting ? 'Saving...' : `Save ${rows.filter((r) => !isRowEmpty(r)).length || ''} Record(s)`}
        </button>
      </div>
    </form>
  );
};

export default PercentageTaxForm;